import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Eye, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const MAIN_DOMAIN = 'tokomoe.com';

function buildStoreUrl(store) {
  if (!store) return '';
  if (store.custom_domain) {
    const d = store.custom_domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return `https://${d}`;
  }
  if (store.subdomain) return `https://${store.subdomain}.${MAIN_DOMAIN}`;
  return '';
}

export default function TenantStoreBuilder() {
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get('/tenant/store').then(r => { setStore(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/store', store);
      toast.success('Perubahan disimpan');
    } catch {
      toast.error('Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const storeUrl = buildStoreUrl(store);
  const previewUrl = storeUrl ? `${storeUrl}?preview=1&t=${Date.now()}` : '';

  const openPreview = () => {
    if (!previewUrl) return;
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const copyUrl = async () => {
    if (!storeUrl) return;
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      toast.success('Link toko disalin');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Gagal menyalin');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div data-testid="tenant-store-builder-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Store Builder</h1>
        {storeUrl && (
          <Button onClick={openPreview} className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm" data-testid="preview-store-btn">
            <Eye size={16} className="mr-2" /> Preview Toko
            <ExternalLink size={12} className="ml-1.5 opacity-80" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle>Informasi Store</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Nama Store</Label><Input value={store?.name || ''} onChange={(e) => setStore({ ...store, name: e.target.value })} data-testid="store-name-input" /></div>
            <div><Label>Deskripsi</Label><Textarea value={store?.description || ''} onChange={(e) => setStore({ ...store, description: e.target.value })} rows={3} /></div>
            <div><Label>Logo URL</Label><Input value={store?.logo_url || ''} onChange={(e) => setStore({ ...store, logo_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Warna Utama</Label><div className="flex gap-2 items-center"><Input type="color" value={store?.primary_color || '#2D6CDF'} onChange={(e) => setStore({ ...store, primary_color: e.target.value })} className="w-16 h-10 p-1" /><Input value={store?.primary_color || '#2D6CDF'} onChange={(e) => setStore({ ...store, primary_color: e.target.value })} className="flex-1" /></div></div>
            <div><Label>Template</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {['commerce', 'course', 'high_ticket'].map(t => (
                  <div key={t} onClick={() => setStore({ ...store, template: t })} className={`p-4 border-2 rounded-lg cursor-pointer text-center transition-all ${store?.template === t ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`template-${t}`}>
                    <p className="font-medium text-sm capitalize">{t.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle>Pengaturan Store</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Banner Text</Label><Input value={store?.store_settings?.banner_text || ''} onChange={(e) => setStore({ ...store, store_settings: { ...store?.store_settings, banner_text: e.target.value } })} /></div>
            <div><Label>Footer Text</Label><Input value={store?.store_settings?.footer_text || ''} onChange={(e) => setStore({ ...store, store_settings: { ...store?.store_settings, footer_text: e.target.value } })} /></div>
            <div><Label>SEO Title</Label><Input value={store?.store_settings?.seo_title || ''} onChange={(e) => setStore({ ...store, store_settings: { ...store?.store_settings, seo_title: e.target.value } })} /></div>
            <div><Label>SEO Description</Label><Textarea value={store?.store_settings?.seo_description || ''} onChange={(e) => setStore({ ...store, store_settings: { ...store?.store_settings, seo_description: e.target.value } })} rows={2} /></div>
            <div className="p-4 bg-slate-50 rounded-lg text-sm border border-slate-200">
              <p className="font-medium text-slate-700 mb-2">Store URL</p>
              {store?.custom_domain && (
                <a
                  href={`https://${store.custom_domain.replace(/^https?:\/\//, '').replace(/\/$/, '')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-emerald-600 font-mono text-sm hover:underline inline-flex items-center gap-1.5 mb-1"
                >
                  {store.custom_domain} <ExternalLink size={12} />
                </a>
              )}
              <div className="flex items-center gap-2">
                <a
                  href={`https://${store?.subdomain}.${MAIN_DOMAIN}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[#2D6CDF] font-mono text-sm hover:underline inline-flex items-center gap-1.5 flex-1 min-w-0 truncate"
                >
                  <span className="truncate">{store?.subdomain}.{MAIN_DOMAIN}</span>
                  <ExternalLink size={12} className="flex-shrink-0" />
                </a>
                <Button variant="outline" size="icon" onClick={copyUrl} title="Salin link" className="h-8 w-8 shrink-0">
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </Button>
                <Button variant="outline" size="sm" onClick={openPreview} className="shrink-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="preview-store-inline-btn">
                  <Eye size={13} className="mr-1" /> Preview
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">Klik link untuk buka toko di tab baru, atau gunakan tombol Preview untuk lihat dengan setting terbaru.</p>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <Button onClick={save} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-store-btn">
          {saving && <Loader2 size={14} className="animate-spin mr-2" />} Simpan Perubahan
        </Button>
        {storeUrl && (
          <Button variant="outline" onClick={openPreview} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <Eye size={14} className="mr-1.5" /> Preview Toko
          </Button>
        )}
      </div>
    </div>
  );
}
